import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiBell, FiClock, FiAlertCircle, FiCalendar, FiEdit, FiCheckCircle, FiXCircle, FiUserX } from 'react-icons/fi'

const NOTIF_CONFIG = {
  fichaje_incompleto: {
    icon: FiAlertCircle,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    label: 'Fichaje incompleto'
  },
  fichaje_olvidado: {
    icon: FiClock,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    label: 'Fichaje olvidado'
  },
  correccion_solicitada: {
    icon: FiEdit,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    label: 'Corrección solicitada'
  },
  correccion_aprobada: {
    icon: FiCheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    label: 'Corrección aprobada'
  },
  correccion_rechazada: {
    icon: FiXCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    label: 'Corrección rechazada'
  },
  ausencia_solicitada: {
    icon: FiCalendar,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    label: 'Ausencia solicitada'
  },
  ausencia_aprobada: {
    icon: FiCheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    label: 'Ausencia aprobada'
  },
  ausencia_rechazada: {
    icon: FiXCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    label: 'Ausencia rechazada'
  },
  correccion_propuesta_admin: {
    icon: FiEdit,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    label: 'Propuesta de corrección'
  },
  correccion_propuesta_aceptada: {
    icon: FiCheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    label: 'Propuesta aceptada'
  },
  correccion_propuesta_rechazada: {
    icon: FiXCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    label: 'Propuesta rechazada'
  },
  fichaje_en_ausencia: {
    icon: FiUserX,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    label: 'Fichaje en ausencia'
  }
}

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'ahora'
  if (diffMins < 60) return `hace ${diffMins}m`
  if (diffHours < 24) return `hace ${diffHours}h`
  if (diffDays === 1) return 'ayer'
  if (diffDays < 7) return `hace ${diffDays} días`
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
}

const PANEL_WIDTH = 384      // sm:w-96
const PANEL_WIDTH_SM = 320   // w-80 (móvil)
const PANEL_GAP = 8          // separación visual entre botón y panel

const NotificationBell = ({ count, notificaciones, loading, onOpen, onMarkRead, onMarkAllRead, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: PANEL_WIDTH })
  const buttonRef = useRef(null)
  const panelRef = useRef(null)

  // Calcula posición del panel a partir del rect del botón.
  // Renderizado via portal con position:fixed → no lo clipea ningún
  // ancestor con overflow:hidden (ej: la sidebar v2).
  const calcularPosicion = () => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const isNarrow = window.innerWidth < 640
    const width = isNarrow ? PANEL_WIDTH_SM : PANEL_WIDTH
    // Por defecto alineado a la derecha del botón. Si se sale por la
    // izquierda del viewport, encajarlo a la izquierda con margen 8px.
    let left = rect.right - width
    if (left < 8) left = 8
    // Si se sale por la derecha del viewport, ajustar
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8
    const top = rect.bottom + PANEL_GAP
    setPanelPos({ top, left, width })
  }

  // Recalcular al abrir, en resize y al hacer scroll del viewport
  useEffect(() => {
    if (!isOpen) return
    calcularPosicion()
    const handle = () => calcularPosicion()
    window.addEventListener('resize', handle)
    window.addEventListener('scroll', handle, true) // capturar scroll en cualquier ancestor
    return () => {
      window.removeEventListener('resize', handle)
      window.removeEventListener('scroll', handle, true)
    }
  }, [isOpen])

  // Cerrar al hacer click fuera (botón + panel)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target)
      const inPanel = panelRef.current && panelRef.current.contains(e.target)
      if (!inButton && !inPanel) setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = () => {
    const newState = !isOpen
    setIsOpen(newState)
    if (newState && onOpen) onOpen()
  }

  return (
    <>
      {/* Botón campana */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notificaciones"
      >
        <FiBell className={`h-5 w-5 ${count > 0 ? 'text-blue-600' : 'text-gray-500'}`} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Panel dropdown vía portal — fuera del clip de la sidebar */}
      {isOpen && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: `${panelPos.top}px`,
            left: `${panelPos.left}px`,
            width: `${panelPos.width}px`,
            maxHeight: 'calc(100vh - 24px)',
          }}
          className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-[60] flex flex-col"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h3 className="font-semibold text-gray-900 text-sm">Notificaciones</h3>
            {count > 0 && (
              <button
                onClick={() => {
                  onMarkAllRead()
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          {/* Lista de notificaciones */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                <p className="text-sm text-gray-500">Cargando...</p>
              </div>
            ) : notificaciones.length === 0 ? (
              <div className="p-8 text-center">
                <FiBell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No hay notificaciones</p>
              </div>
            ) : (
              notificaciones.map(notif => {
                const config = NOTIF_CONFIG[notif.tipo] || {
                  icon: FiBell, color: 'text-gray-500', bgColor: 'bg-gray-50', label: notif.tipo
                }
                const Icon = config.icon

                return (
                  <div
                    key={notif.id}
                    onClick={() => {
                      if (!notif.leida) onMarkRead(notif.id)
                      if (onNavigate) onNavigate(notif)
                      setIsOpen(false)
                    }}
                    className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
                      notif.leida
                        ? 'opacity-60 bg-gray-50 hover:bg-gray-100'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${config.bgColor} flex-shrink-0`}>
                        <Icon className={`h-4 w-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {formatTimeAgo(notif.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mt-0.5 line-clamp-2">{notif.mensaje}</p>
                      </div>
                      {!notif.leida && (
                        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default NotificationBell
